import { createFileRoute } from '@tanstack/react-router';
import { Award, Construction, Ticket, Trophy, Users } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { LedgerTable, type LedgerEntry } from '@/components/data/LedgerTable';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { CopyableId } from '@/components/primitives/CopyableId';
import { EntityChip } from '@/components/primitives/EntityChip';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import { recordRecentEntity } from '@/features/recents/recent-entities';
import {
  PARTICIPATION_LABELS,
  useSquadDetail,
  type SquadDetail,
  type SquadLeagueEntry,
} from '@/features/squads/squads';

export const Route = createFileRoute('/squads/$squadId')({
  component: SquadDetailScreen,
});

function SquadDetailScreen() {
  const { squadId } = Route.useParams();
  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } =
    useSquadDetail(squadId);

  useEffect(() => {
    if (!data) return;
    recordRecentEntity({
      kind: 'squad',
      id: data.id,
      name: data.name,
      to: `/squads/${data.id}`,
    });
  }, [data]);

  if (isError) {
    return (
      <RequireCapability capability="squads.view">
        <ErrorState error={error} subject="this squad" onRetry={() => void refetch()} />
      </RequireCapability>
    );
  }
  if (isPending) {
    return (
      <RequireCapability capability="squads.view">
        <div className="flex flex-col gap-3">
          <Skeleton height={100} />
          <Skeleton height={200} />
        </div>
      </RequireCapability>
    );
  }

  return (
    <RequireCapability capability="squads.view">
      <div className="flex flex-col gap-4">
        <Header
          squad={data}
          isFetching={isFetching}
          dataUpdatedAt={dataUpdatedAt}
          onRefresh={() => void refetch()}
        />

        <div className="grid gap-4 lg:grid-cols-2">
          <MembersPanel squad={data} />
          <InvitesPanel squad={data} />
        </div>

        <LeagueEntriesPanel squad={data} />
        <SeasonRecordPanel squad={data} />
        <LegacyLedgerPanel squad={data} />
        <ActionsPanel />
      </div>
    </RequireCapability>
  );
}

function Header({
  squad,
  isFetching,
  dataUpdatedAt,
  onRefresh,
}: {
  squad: SquadDetail;
  isFetching: boolean;
  dataUpdatedAt: number;
  onRefresh: () => void;
}) {
  return (
    <div className="border-bd bg-card flex flex-col gap-2.5 rounded-xl border p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {squad.imageUrl ? (
            <img
              src={squad.imageUrl}
              alt=""
              className="border-bd size-10 flex-none rounded-lg border object-cover"
            />
          ) : (
            <span
              aria-hidden
              className="grid size-10 flex-none place-items-center rounded-lg font-mono text-[11px] font-bold"
              style={{ background: 'rgba(var(--ok-rgb), 0.18)', color: 'var(--ok)' }}
            >
              SQ
            </span>
          )}
          <div className="min-w-0">
            <h1 className="font-head text-tx truncate text-[22px] leading-none font-semibold">
              {squad.name}
            </h1>
            {squad.tagline && (
              <p className="text-tx3 mt-1 truncate text-[12px] italic">{squad.tagline}</p>
            )}
          </div>
        </div>
        <LiveDataIndicator
          updatedAt={dataUpdatedAt || undefined}
          isFetching={isFetching}
          onRefresh={onRefresh}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {squad.lifecycleStatus && (
          <StateChip
            state={squad.lifecycleStatus}
            size="sm"
            tone={squad.lifecycleStatus === 'archived' ? 'muted' : 'good'}
          />
        )}
        {squad.isPublic !== null && (
          <StateChip
            state={squad.isPublic ? 'public' : 'private'}
            size="sm"
            tone="neutral"
            label={squad.isPublic ? 'Public' : 'Private'}
          />
        )}
        {squad.affiliation?.isGreek && (
          <StateChip
            state="greek"
            size="sm"
            tone="live"
            label={squad.affiliation.fraternity || 'Greek'}
          />
        )}
        <span className="text-tx3 text-[11.5px] capitalize">
          {squad.sportEligibility.join(', ') || 'no sports'}
        </span>
        <CopyableId value={squad.id} label="Squad ID" truncate />
      </div>

      {/* Mascots belong to the squad, which is why they live here and not on a user. */}
      {(squad.activeMascot ?? squad.unlockedMascots.length > 0) && (
        <p className="text-tx3 text-[11.5px]">
          Squad-owned mascots: {squad.unlockedMascots.length || 'none'}
          {squad.activeMascot ? ` · equipped ${squad.activeMascot}` : ''}
        </p>
      )}
    </div>
  );
}

function MembersPanel({ squad }: { squad: SquadDetail }) {
  const current = squad.members.filter((member) => member.status === 'active');
  const past = squad.members.filter((member) => member.status !== 'active');

  return (
    <Panel title="Members" icon={Users} count={current.length}>
      {squad.members.length === 0 ? (
        <EmptyState title="No members" description="Nobody is in this squad." />
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="flex flex-col gap-1.5">
            {current.map((member) => (
              <li
                key={member.userId}
                className="border-bd/60 flex flex-wrap items-center justify-between gap-2 border-b pb-1.5 last:border-b-0 last:pb-0"
              >
                <EntityChip
                  kind="user"
                  id={member.userId}
                  name={member.name}
                  secondary={member.username ? `@${member.username}` : undefined}
                  imageUrl={member.avatarUrl ?? undefined}
                  to={`/users/${member.userId}`}
                />
                <div className="flex items-center gap-1">
                  {member.role && (
                    <StateChip
                      state={member.role}
                      size="sm"
                      tone={member.role === 'owner' ? 'warn' : 'neutral'}
                    />
                  )}
                  {member.canEquipMascot && (
                    <span className="text-tx3 text-[10px]" title="Can equip the squad mascot">
                      ★
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {/* Rows are flipped to left/removed, never deleted — so past members persist. */}
          {past.length > 0 && (
            <details>
              <summary className="text-tx3 hover:text-tx2 cursor-pointer text-[11.5px]">
                {past.length} former {past.length === 1 ? 'member' : 'members'}
              </summary>
              <ul className="mt-2 flex flex-col gap-1.5 opacity-70">
                {past.map((member) => (
                  <li
                    key={member.userId}
                    className="flex flex-wrap items-center justify-between gap-2"
                  >
                    <EntityChip
                      kind="user"
                      id={member.userId}
                      name={member.name}
                      to={`/users/${member.userId}`}
                    />
                    <div className="flex items-center gap-1.5">
                      <StateChip state={member.status ?? 'unknown'} size="sm" tone="muted" />
                      <span className="mesh-mono text-tx3 text-[11px]">
                        {member.removedAt ? member.removedAt.slice(0, 10) : '—'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </Panel>
  );
}

function InvitesPanel({ squad }: { squad: SquadDetail }) {
  const usable = squad.invites.filter((invite) => !invite.isRevoked && !invite.isExpired);

  return (
    <Panel title="Invites" icon={Ticket} count={usable.length}>
      {squad.invites.length === 0 ? (
        <EmptyState title="No invite codes" description="This squad has no invite codes." />
      ) : (
        <ul className="flex flex-col gap-1.5">
          {squad.invites.map((invite) => (
            <li
              key={invite.id}
              className="border-bd/60 flex flex-wrap items-center justify-between gap-2 border-b pb-1.5 last:border-b-0 last:pb-0"
            >
              <span className="mesh-mono text-tx text-[12.5px]">{invite.code ?? '—'}</span>
              <div className="flex items-center gap-1.5">
                <span className="mesh-mono text-tx3 text-[11px]">used {invite.useCount}×</span>
                <StateChip
                  state={invite.isRevoked ? 'revoked' : invite.isExpired ? 'expired' : 'active'}
                  size="sm"
                  tone={invite.isRevoked || invite.isExpired ? 'muted' : 'good'}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function LeagueEntriesPanel({ squad }: { squad: SquadDetail }) {
  return (
    <Panel title="League entries" icon={Trophy} count={squad.leagueEntries.length}>
      {squad.leagueEntries.length === 0 ? (
        <EmptyState
          title="No league entries"
          description="This squad has never entered a league."
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {squad.leagueEntries.map((entry) => (
            <EntryRow key={entry.id} entry={entry} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function EntryRow({ entry }: { entry: SquadLeagueEntry }) {
  // `expired` and `auto_opted_out` mean the member never answered, which is a
  // different fact from opting out — and it decides philanthropy eligibility, so the
  // unanswered count is called out separately rather than folded into "not in".
  const unanswered = entry.participation.filter(
    (row) =>
      row.status === 'expired' || row.status === 'auto_opted_out' || row.status === 'pending',
  ).length;
  const optedIn = entry.participation.filter((row) => row.status === 'opted_in').length;

  return (
    <li className="border-bd/60 flex flex-col gap-1.5 border-b pb-3 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <EntityChip
          kind="league"
          id={entry.leagueId}
          name={entry.leagueName}
          secondary={entry.sport ?? undefined}
          imageUrl={entry.leagueImageUrl ?? undefined}
          to={`/leagues/${entry.leagueId}`}
        />
        <div className="flex items-center gap-1.5">
          {entry.status && <StateChip state={entry.status} size="sm" />}
          <span className="mesh-mono text-tx3 text-[11px]">
            {optedIn}/{entry.participation.length} opted in
          </span>
        </div>
      </div>

      {unanswered > 0 && (
        <p className="text-[11.5px]" style={{ color: 'var(--warn)' }}>
          {unanswered} member{unanswered === 1 ? '' : 's'} never answered the opt-in. That is
          not the same as opting out and it affects philanthropy eligibility.
        </p>
      )}

      {entry.participation.length > 0 && (
        <details>
          <summary className="text-tx3 hover:text-tx2 cursor-pointer text-[11.5px]">
            Per-member decisions
          </summary>
          <ul className="mt-1.5 flex flex-col gap-1">
            {entry.participation.map((row) => (
              <li key={row.userId} className="flex flex-wrap items-center gap-2">
                <span className="text-tx2 min-w-0 flex-1 truncate text-[12px]">{row.name}</span>
                <StateChip
                  state={row.status}
                  size="sm"
                  label={PARTICIPATION_LABELS[row.status] ?? row.status}
                  tone={
                    row.status === 'opted_in'
                      ? 'good'
                      : row.status === 'opted_out' || row.status === 'sat_out'
                        ? 'neutral'
                        : 'warn'
                  }
                />
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
}

function SeasonRecordPanel({ squad }: { squad: SquadDetail }) {
  return (
    <Panel title="Season record" icon={Award} count={squad.seasonRollups.length}>
      {squad.seasonRollups.length === 0 ? (
        <EmptyState
          title="No completed seasons"
          description="This squad has not finished a season yet."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-max border-collapse text-left">
            <caption className="sr-only">Season-by-season record</caption>
            <thead>
              <tr className="border-bd border-b">
                {['Season', 'League', 'W–L–T', 'PF', 'PA', 'Rank', 'Titles'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="font-head text-tx3 py-1.5 pr-4 text-[10.5px] font-semibold uppercase"
                    style={{ letterSpacing: '0.14em' }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {squad.seasonRollups.map((rollup) => (
                <tr key={rollup.id} className="border-bd/60 border-b last:border-b-0">
                  <td className="text-tx2 py-2 pr-4 text-[12.5px]">
                    {rollup.seasonLabel ?? '—'}
                    {/* A partial season is not a bad record; it is an incomplete one. */}
                    {rollup.departedAtWeek !== null && (
                      <span className="text-tx3 ml-1.5 text-[11px]">
                        left W{rollup.departedAtWeek}
                      </span>
                    )}
                  </td>
                  <td className="text-tx3 py-2 pr-4 text-[12px]">{rollup.leagueName ?? '—'}</td>
                  <td className="mesh-mono text-tx py-2 pr-4 text-[12.5px]">
                    {rollup.wins}–{rollup.losses}–{rollup.ties}
                  </td>
                  <td className="mesh-mono text-tx3 py-2 pr-4 text-[12px]">
                    {rollup.pointsFor.toLocaleString()}
                  </td>
                  <td className="mesh-mono text-tx3 py-2 pr-4 text-[12px]">
                    {rollup.pointsAgainst.toLocaleString()}
                  </td>
                  <td className="mesh-mono text-tx2 py-2 pr-4 text-[12px]">
                    {rollup.finalRank ?? '—'}
                  </td>
                  <td className="mesh-mono py-2 pr-4 text-[12px]">
                    {rollup.championships > 0 ? (
                      <span style={{ color: 'var(--ok)' }}>{rollup.championships}</span>
                    ) : (
                      <span className="text-tx3">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function LegacyLedgerPanel({ squad }: { squad: SquadDetail }) {
  // XP is the ledger's primary quantity; gems ride along in the reason line so the
  // shared LedgerTable can render both without a second table.
  const entries: LedgerEntry[] = squad.legacyLedger.entries.map((entry) => ({
    id: entry.id,
    occurredAt: entry.occurredAt?.replace('T', ' ').slice(0, 19) ?? '—',
    delta: entry.xpDelta,
    balance: entry.xpAfter,
    reason: entry.eventType,
    sourceType: entry.gemsDelta
      ? `${entry.sourceType} · ${entry.gemsDelta > 0 ? '+' : ''}${String(entry.gemsDelta)} gems`
      : entry.sourceType,
  }));

  return (
    <Panel
      title="Legacy ledger"
      count={squad.legacyLedger.entries.length}
      actions={
        <span className="mesh-mono text-tx text-[13px]">
          {squad.legacyLedger.totalXp.toLocaleString()}
          <span className="text-tx3 ml-1 text-[11px]">XP</span>
          <span className="text-tx3 mx-1.5">·</span>
          {squad.legacyLedger.totalGems.toLocaleString()}
          <span className="text-tx3 ml-1 text-[11px]">gems</span>
        </span>
      }
    >
      {squad.legacyLedger.truncated && (
        <p className="text-tx3 mb-2 text-[11.5px]">
          Showing the most recent {squad.legacyLedger.entries.length} entries; totals cover only
          those.
        </p>
      )}
      {entries.length === 0 ? (
        <EmptyState
          title="Nothing on the record"
          description="This squad has earned no XP or gems yet."
        />
      ) : (
        <LedgerTable entries={entries} unit="XP" label="legacy ledger" />
      )}
    </Panel>
  );
}

function ActionsPanel() {
  return (
    <Panel title="Squad actions">
      <StateBlock
        icon={Construction}
        tone="info"
        title="Read-only for now"
        description="Renaming, archiving, removing members and revoking invites are not wired. A squad write has to republish to the squad's live members, so it belongs in a delegate operation — none exists yet."
      />
    </Panel>
  );
}

function Panel({
  title,
  icon: Icon,
  count,
  actions,
  children,
}: {
  title: string;
  icon?: typeof Users;
  count?: number;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <h2
          className="font-head text-tx2 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
          style={{ letterSpacing: '0.16em' }}
        >
          {Icon && <Icon size={12} aria-hidden />}
          {title}
          {count !== undefined && (
            <span className="mesh-mono text-tx3 text-[11px]">{count}</span>
          )}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}
